import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const styles = {
  container: { padding: '20px' },
  filters: { display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'center', flexWrap: 'wrap' },
  endMonthRow: { display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', marginBottom: '12px' },
  endMonthLabel: { fontSize: '0.9rem', color: '#444', marginRight: '6px' },
  tabBtn: (active) => ({
    padding: '6px 10px',
    border: active ? '2px solid #1976d2' : '1px solid #ccc',
    borderRadius: '6px',
    background: active ? '#e3f2fd' : '#fff',
    cursor: 'pointer',
    fontSize: '0.85rem',
  }),
  input: { padding: '8px' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { border: '1px solid #ddd', padding: '8px', background: '#f4f4f4' },
  td: { border: '1px solid #ddd', padding: '8px' },
  noticeBox: {
    background: '#fff8e6',
    border: '1px solid #ffe082',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '16px',
    fontSize: '0.9rem',
    color: '#5d4e00',
    lineHeight: 1.6,
  },
};

/** 정산 취소 시 참여종료일이 자동 복구되지 않음 — 확인창·안내에 공통 사용 */
const SETTLE_CANCEL_ENDDATE_NOTICE =
  '정산 취소로 참여 중으로 전환되면, 참여종료일은 이전 값으로 자동 복구되지 않습니다. 필요할 경우 참여자 관리에서 참여종료일을 직접 수정해 주세요.';

const SettleCancelPage = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [projectId, setProjectId] = useState('');
  const [workplace, setWorkplace] = useState('');
  const [searchName, setSearchName] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [exportLoading, setExportLoading] = useState(false);
  /** null = 조회 연도 내 종료월 전체, 1~12 = 해당 종료월만 */
  const [endMonth, setEndMonth] = useState(null);

  const fetchProjects = useCallback(async () => {
    try {
      const res = await api.get(`/projects?year=${year}`);
      setProjects(res.data);
    } catch (err) {
      setProjects([]);
    }
  }, [year]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const buildSearchParams = () => {
    const params = {
      year,
      projectId,
      workplace,
      searchName,
      status: 'ended',
      endDateYear: year,
    };
    if (endMonth != null && endMonth >= 1 && endMonth <= 12) {
      params.endDateMonth = endMonth;
    }
    return params;
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const res = await api.get('/report/batch-search', { params: buildSearchParams() });
      const filtered = res.data.filter((r) => r.status === 'ended');
      setResults(filtered);
      setSelectedIds(new Set());
    } catch (err) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === results.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(results.map((r) => r.participantId)));
    }
  };

  const handleCancelBatch = async () => {
    if (selectedIds.size === 0) return alert('하나 이상 선택하세요.');
    if (
      !window.confirm(
        `선택된 ${selectedIds.size}명의 정산을 일괄 취소하시겠습니까?\n\n※ ${SETTLE_CANCEL_ENDDATE_NOTICE}`,
      )
    )
      return;
    try {
      const participantIds = Array.from(selectedIds);
      const res = await api.put('/settle/batch-cancel', { participantIds, year });
      alert(res.data.message || '일괄 정산 취소 완료');
      setSelectedIds(new Set());
      handleSearch();
    } catch (err) {
      alert(err.response?.data?.message || '일괄 정산 취소 실패');
    }
  };

  const handleExport = async () => {
    if (results.length === 0) {
      alert('엑셀로 보낼 데이터가 없습니다.');
      return;
    }
    if (exportLoading) return;

    setExportLoading(true);
    try {
      const response = await api.get('/settle/cancel/export', { params: buildSearchParams(), responseType: 'blob' });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const monthSuffix = endMonth != null ? `_m${endMonth}` : '';
      link.download = `settle_cancel_${year}${monthSuffix}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (err.response?.status === 404) {
        alert(err.response.data?.message || '엑셀로 보낼 데이터가 없습니다.');
      } else {
        alert(err.response?.data?.message || '엑셀 출력 중 오류가 발생했습니다.');
      }
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <h2>정산 조회 및 취소</h2>

      <div style={styles.noticeBox}>
        <strong>참여종료일 안내</strong>
        <br />
        {SETTLE_CANCEL_ENDDATE_NOTICE}
      </div>
      <div style={styles.filters}>
        <input type="number" style={styles.input} value={year} onChange={(e) => setYear(parseInt(e.target.value || 0, 10))} />
        <select style={styles.input} value={projectId} onChange={(e) => setProjectId(e.target.value)}>
          <option value="">-- 전체 사업 --</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.projectName}
            </option>
          ))}
        </select>
      </div>
      <div style={styles.endMonthRow}>
        <span style={styles.endMonthLabel}>종료월</span>
        <button type="button" style={styles.tabBtn(endMonth == null)} onClick={() => setEndMonth(null)}>
          전체
        </button>
        {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
          <button key={m} type="button" style={styles.tabBtn(endMonth === m)} onClick={() => setEndMonth(m)}>
            {m}월
          </button>
        ))}
      </div>
      <div style={styles.filters}>
        <input placeholder="근무장소" style={styles.input} value={workplace} onChange={(e) => setWorkplace(e.target.value)} />
        <input placeholder="성명" style={styles.input} value={searchName} onChange={(e) => setSearchName(e.target.value)} />
        <button onClick={handleSearch} style={{ padding: '8px 12px' }}>
          {loading ? '조회중...' : '조회'}
        </button>
        <div style={{ display: 'flex', gap: '10px', marginLeft: 12 }}>
          <button
            onClick={handleExport}
            style={{
              padding: '8px 12px',
              background: '#1976d2',
              color: '#fff',
              border: 'none',
              borderRadius: 3,
              cursor: exportLoading ? 'default' : 'pointer',
            }}
            disabled={exportLoading || loading || results.length === 0}
          >
            {exportLoading ? '엑셀 생성 중...' : '엑셀 출력'}
          </button>
          <button
            onClick={handleCancelBatch}
            style={{ padding: '8px 12px', background: '#ff9800', color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer' }}
          >
            선택 정산취소
          </button>
        </div>
      </div>

      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>
              <input type="checkbox" checked={selectedIds.size === results.length && results.length > 0} onChange={selectAll} />
            </th>
            <th style={styles.th}>사업명</th>
            <th style={styles.th}>성명</th>
            <th style={styles.th}>주민번호</th>
            <th style={styles.th}>근무장소</th>
            <th style={styles.th}>발생 연차</th>
            <th style={styles.th}>사용 연차</th>
            <th style={styles.th}>정산연차시간</th>
            <th style={styles.th}>정산금액</th>
          </tr>
        </thead>
        <tbody>
          {results.map((r) => (
            <tr key={r.participantId}>
              <td style={styles.td}>
                <input type="checkbox" checked={selectedIds.has(r.participantId)} onChange={() => toggleSelect(r.participantId)} />
              </td>
              <td style={styles.td}>{r.projectName}</td>
              <td style={styles.td}>{r.name}</td>
              <td style={styles.td}>{String(r.rrn).substring(0, 8)}...</td>
              <td style={styles.td}>{r.workplace}</td>
              <td style={styles.td}>{r.totalGeneratedHours}h</td>
              <td style={styles.td}>{r.cumulativeUsedHours}h</td>
              <td style={styles.td}>{r.status === 'ended' ? `${r.totalPaidOutHours}h` : `${r.remainingUnpaidHours}h`}</td>
              <td style={styles.td}>
                {r.status === 'ended'
                  ? `${(r.totalPaidOutAmount || 0).toLocaleString()}원`
                  : `${(r.settleAmount || 0).toLocaleString()}원`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default SettleCancelPage;
